import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ArrowRight, Trash2, Split, Heart, Crown, ChevronDown, Droplets, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase-client';
import { useSettingsContext } from '../../lib/SettingsContext';
import { useRenderOptions } from '../hooks/useRenderOptions';
import {
  normalizeWatermarkSettings,
  getWatermarkStyles,
  calculateWatermarkSize,
} from '../../lib/watermark-config';
import { Switch } from './ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { DeleteFileDialog } from './delete-file-dialog';
import { VisuallyHidden } from './ui/visually-hidden';
import {
  CommandDialog,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command';
import type { RenderVariant, RenderSettings } from '../../types/property';
import { useImageDimensions } from '../hooks/useImageDimensions';

// Module-level cache for Wysko logo signed URL (avoids re-fetching on every modal open)
let cachedWyskoLogoUrl: string | null = null;
let cachedWyskoLogoExpiry = 0;
const WYSKO_LOGO_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface ImageMetadata {
  width: number;
  height: number;
  format: string;
  source: string;
  createdAt: string;
}

interface PropertyOption {
  id: string;
  address: string;
  photoCount: number;
  imageUrl: string;
}

interface RenderPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyAddress: string;
  roomName: string;
  variants: RenderVariant[];
  initialVariantIndex?: number;
  originalUrl?: string;
  renderSettings?: RenderSettings;
  onDelete?: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  availableProperties?: PropertyOption[];
  currentPropertyId?: string;
  onMove?: (targetPropertyId: string) => void;
  onRerender?: () => void;
  onAutoSaveAndClose?: () => void;
  generationId?: string;
  onRetryVariant?: () => void;
  isRetryingVariant?: boolean;
}

// Helper to get AI model display name
const getModelDisplayName = (source?: string): string => {
  if (!source) return 'Nieznany';
  if (source.includes('gemini')) return 'Model dokładny';
  if (source.includes('nanobanana2')) return 'Model kreatywny';
  if (source.includes('openai')) return 'Model kreatywny'; // Legacy
  return source;
};

export function RenderPreview({
  open,
  onOpenChange,
  propertyAddress,
  roomName,
  variants,
  initialVariantIndex = 0,
  originalUrl,
  renderSettings,
  onDelete,
  isFavorite,
  onToggleFavorite,
  availableProperties = [],
  currentPropertyId,
  onMove,
  onRerender,
  onAutoSaveAndClose,
  generationId,
  onRetryVariant,
  isRetryingVariant,
}: RenderPreviewProps) {
  const [currentVariantIndex, setCurrentVariantIndex] = useState(initialVariantIndex);

  // Reset variant index when modal opens to prevent stale index from previous usage
  useEffect(() => {
    if (open) {
      setCurrentVariantIndex(initialVariantIndex);
    }
  }, [open, initialVariantIndex]);

  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Watermark settings - using global context for immediate updates
  const { settings } = useSettingsContext();
  const { stagingPersonas, renovationStyles } = useRenderOptions();
  const [includeWyskoWatermark, setIncludeWyskoWatermark] = useState(true);
  const [includeAgencyWatermark, setIncludeAgencyWatermark] = useState(true);
  const [wyskoLogoUrl, setWyskoLogoUrl] = useState<string | null>(null);

  // Image dimension tracking for proper watermark positioning
  const mainImageRef = useRef<HTMLImageElement>(null);
  const comparisonImageRef = useRef<HTMLImageElement>(null);

  // Note: Settings are now managed by SettingsContext and automatically stay in sync
  // No need to manually refresh when modal opens

  // Initialize watermark toggles from user settings when modal opens
  useEffect(() => {
    if (open && settings) {
      setIncludeWyskoWatermark(settings.wysko_watermark_enabled || false);
      setIncludeAgencyWatermark(settings.watermark_enabled || false);
    }
  }, [open, settings]);

  // Fetch Wysko logo when modal opens (cached for 10 min)
  useEffect(() => {
    const loadWyskoLogo = async () => {
      if (!open) return;

      // Use cached URL if still valid
      if (cachedWyskoLogoUrl && Date.now() < cachedWyskoLogoExpiry) {
        setWyskoLogoUrl(cachedWyskoLogoUrl);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/server/gcs/sign-urls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ items: [{ bucket: 'wysko-assets', path: 'branding/wysko-watermark.png' }] })
        });

        if (response.ok) {
          const result = await response.json();
          const url = result.data?.['branding/wysko-watermark.png'] || null;
          setWyskoLogoUrl(url);
          cachedWyskoLogoUrl = url;
          cachedWyskoLogoExpiry = Date.now() + WYSKO_LOGO_CACHE_TTL;
        }
      } catch (error) {
        console.error('Failed to load Wysko logo:', error);
      }
    };

    loadWyskoLogo();
  }, [open]);

  /* Variants list — no fake fallback; empty state handled in UI */
  const effectiveVariants = (variants && variants.length > 0) ? variants : [];
  const hasNoVariants = effectiveVariants.length === 0 && !!originalUrl;

  /* Safe access to current variant */
  const currentVariant = effectiveVariants.length > 0 ? effectiveVariants[currentVariantIndex] : null;

  // Track image dimensions for watermark positioning (shared hook replaces duplicate ResizeObserver effects)
  const mainImageDimensions = useImageDimensions(mainImageRef, [currentVariant?.url, isComparisonMode]);
  const comparisonImageDimensions = useImageDimensions(comparisonImageRef, [currentVariant?.url, isComparisonMode], isComparisonMode);

  // Normalize settings from DB format for watermark rendering
  const normalizedSettings = normalizeWatermarkSettings(settings || {}, 'db');

  // Render watermark overlay for a given image
  const renderWatermarkOverlay = (
    dimensions: { width: number; height: number } | null,
    includeAgency: boolean,
    includeWysko: boolean
  ) => {
    if (!dimensions) return null;

    const agencyEnabled = includeAgency && !!settings?.watermark_logo_url;
    const wyskoEnabled = includeWysko && !!wyskoLogoUrl;

    if (!agencyEnabled && !wyskoEnabled) return null;

    // Calculate proportional sizes based on displayed image dimensions
    const agencySize = agencyEnabled
      ? calculateWatermarkSize(dimensions.width, dimensions.height, 'agency', normalizedSettings.agency.scale)
      : null;

    const wyskoSize = wyskoEnabled
      ? calculateWatermarkSize(dimensions.width, dimensions.height, 'wysko', normalizedSettings.wysko.scale)
      : null;

    return (
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        {/* Agency watermark */}
        {agencyEnabled && agencySize && (
          <div
            style={getWatermarkStyles(
              dimensions.width,
              dimensions.height,
              {
                ...normalizedSettings.agency,
                enabled: true,
              },
              false,
              undefined,
              10
            )}
          >
            <img
              src={settings!.watermark_logo_url!}
              alt=""
              style={{ maxHeight: `${agencySize.maxHeight}px`, maxWidth: `${agencySize.maxWidth}px` }}
              className="object-contain"
            />
          </div>
        )}

        {/* Wysko watermark */}
        {wyskoEnabled && wyskoSize && (
          <div
            style={getWatermarkStyles(
              dimensions.width,
              dimensions.height,
              {
                ...normalizedSettings.wysko,
                enabled: true,
              },
              agencyEnabled, // is secondary if agency also shown
              normalizedSettings.agency.position,
              11
            )}
          >
            <img
              src={wyskoLogoUrl!}
              alt=""
              style={{ maxHeight: `${wyskoSize.maxHeight}px`, maxWidth: `${wyskoSize.maxWidth}px` }}
              className="object-contain"
            />
          </div>
        )}
      </div>
    );
  };

  // Fetch image metadata when variant changes
  const fetchImageMetadata = useCallback(async (variant: typeof currentVariant) => {
    if (!variant) {
      setImageMetadata(null);
      return;
    }

    setIsLoadingMetadata(true);
    try {
      // Create an image element to get dimensions (without crossOrigin to avoid CORS)
      const img = new Image();

      const dimensionsPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = variant.url;
      });

      const dimensions = await dimensionsPromise;

      // Determine format from URL or default to PNG
      const urlLower = variant.url.toLowerCase();
      let format = 'PNG';
      if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) format = 'JPEG';
      else if (urlLower.includes('.webp')) format = 'WebP';
      else if (urlLower.includes('.png')) format = 'PNG';

      setImageMetadata({
        width: dimensions.width,
        height: dimensions.height,
        format,
        source: getModelDisplayName(variant.source),
        createdAt: new Date().toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).replace(/\//g, '.'),
      });
    } catch (error) {
      console.error('Failed to fetch image metadata:', error);
      setImageMetadata(null);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, []);

  // Fetch metadata when current variant changes
  useEffect(() => {
    if (open && currentVariant) {
      fetchImageMetadata(currentVariant);
    }
  }, [open, currentVariant, fetchImageMetadata]);

  // Download image in specified format via proxy endpoint
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadAs = useCallback(async (format: 'png' | 'jpg' | 'webp') => {
    if (!currentVariant || isDownloading) return;

    setIsDownloading(true);
    try {
      // Get auth session for the request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found for download');
        return;
      }

      const proxyUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-image`);
      proxyUrl.searchParams.set('url', currentVariant.url);
      proxyUrl.searchParams.set('format', format);
      const filename = `${propertyAddress}_${roomName}_wariant_${currentVariantIndex + 1}.${format}`;
      proxyUrl.searchParams.set('filename', filename);

      // Watermark parameters
      proxyUrl.searchParams.set('applyWyskoWatermark', includeWyskoWatermark.toString());
      proxyUrl.searchParams.set('applyAgencyWatermark', includeAgencyWatermark.toString());

      // Fetch with auth header
      const response = await fetch(proxyUrl.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [currentVariant, propertyAddress, roomName, currentVariantIndex, isDownloading, includeWyskoWatermark, includeAgencyWatermark]);

  if (!open) return null;

  /* If no variants AND no originalUrl, show loader */

  const handlePrevious = () => {
    setCurrentVariantIndex((prev) => (prev > 0 ? prev - 1 : effectiveVariants.length - 1));
  };

  const handleNext = () => {
    setCurrentVariantIndex((prev) => (prev < effectiveVariants.length - 1 ? prev + 1 : 0));
  };

  // Generate tags from renderSettings
  const generateSettingsTags = () => {
    const tags: string[] = [];

    if (!renderSettings) return tags;

    // Mode tag
    const modeLabels = {
      enhance: 'Poprawa jakości',
      staging: 'Wirtualne meblowanie',
      renovation: 'Renowacja',
    };
    tags.push(modeLabels[renderSettings.mode]);

    // Enhancement options
    if (renderSettings.mode === 'enhance') {
      if (renderSettings.autoRetouch) tags.push('Automatyczny retusz');
      if (renderSettings.addAccessories) tags.push('Dodanie akcesoriów');
    }

    // Staging persona (dynamic from DB via useRenderOptions)
    if (renderSettings.mode === 'staging' && renderSettings.persona) {
      const persona = stagingPersonas.find(p => p.option_key === renderSettings.persona);
      tags.push(persona ? `Persona: ${persona.name}` : renderSettings.persona);
    }

    // Renovation style (dynamic from DB via useRenderOptions)
    if (renderSettings.mode === 'renovation' && renderSettings.style) {
      const style = renovationStyles.find(s => s.option_key === renderSettings.style);
      tags.push(style ? `Styl: ${style.name}` : renderSettings.style);
    }

    return tags;
  };

  const settingsTags = generateSettingsTags();

  const handleCloseAttempt = () => {
    if (!currentPropertyId) {
      setIsCloseConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) handleCloseAttempt();
      }}>
        <DialogContent hideCloseButton={true} className="w-full h-full lg:max-w-[95vw] max-w-[100vw] lg:h-[90vh] p-0 gap-0 bg-background lg:bg-transparent border-none flex flex-col lg:flex-row overflow-hidden rounded-none lg:rounded-lg">
          <VisuallyHidden>
            <DialogTitle>Podgląd renderów - {propertyAddress} {roomName}</DialogTitle>
            <DialogDescription>
              Przeglądaj i pobierz wygenerowane warianty renderów dla {propertyAddress}, pomieszczenie: {roomName}
            </DialogDescription>
          </VisuallyHidden>

          <div className="flex flex-col lg:flex-row h-full w-full gap-0">
            {/* MOBILE HEADER - Room name and close */}
            <div className="lg:hidden flex items-center justify-between h-14 px-4 bg-card border-b border-border flex-shrink-0">
              <div className="flex-1 min-w-0">
                <h2 className="text-[15px] font-semibold truncate">{roomName}</h2>
                <p className="text-[11px] text-muted-foreground truncate">{propertyAddress}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseAttempt();
                }}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>

            {/* LEFT COLUMN - The Stage */}
            <div className="relative w-full h-[45vh] lg:w-[75%] lg:h-full bg-[#0F0F0F] flex items-center justify-center group flex-shrink-0">
              {/* Main Image */}
              <div className="relative w-full h-full flex items-center justify-center p-4 lg:p-8">
                {/* No variants — show original as preview */}
                {hasNoVariants ? (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <img
                      src={originalUrl}
                      alt={`${propertyAddress} - ${roomName} - Oryginał`}
                      className="max-w-full max-h-full object-contain"
                    />
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white/80 text-xs">
                      Oryginał
                    </div>
                  </div>
                ) : (effectiveVariants.length === 0 || !currentVariant) ? (
                  <div className="flex flex-col items-center gap-4 text-white/80">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-lg font-medium">Generowanie wariantów...</p>
                  </div>
                ) : isComparisonMode && originalUrl ? (
                  // Comparison Mode - 50/50 Split
                  <div className="flex w-full h-full max-w-full max-h-full gap-2">
                    {/* Left Pane - Original (Static) */}
                    <div className="flex-1 flex items-center justify-center bg-[#1a1a1a] rounded-lg overflow-hidden">
                      <img
                        src={originalUrl}
                        alt={`${propertyAddress} - ${roomName} - Original`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    {/* Right Pane - Current Variant (Dynamic) */}
                    <div className="relative flex-1 flex items-center justify-center bg-[#1a1a1a] rounded-lg overflow-hidden">
                      {/* Wrapper for image + watermarks - inline-block sizes to image */}
                      <div className="relative inline-block">
                        <img
                          ref={comparisonImageRef}
                          src={currentVariant.url}
                          alt={`${propertyAddress} - ${roomName} - Variant ${currentVariantIndex + 1}`}
                          className="block max-w-full max-h-full"
                        />

                        {/* Watermark overlay - fills the wrapper (same size as image) */}
                        {renderWatermarkOverlay(comparisonImageDimensions, includeAgencyWatermark, includeWyskoWatermark)}
                      </div>

                      {/* Previous/Next Navigation - ONLY on Right Pane */}
                      {effectiveVariants.length > 1 && (
                        <>
                          <button
                            onClick={handlePrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all backdrop-blur-sm"
                          >
                            <ChevronLeft className="w-6 h-6 text-white" strokeWidth={2} />
                          </button>
                          <button
                            onClick={handleNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all backdrop-blur-sm"
                          >
                            <ChevronRight className="w-6 h-6 text-white" strokeWidth={2} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  // Default Mode - Full Image
                  <>
                    {/* Wrapper for image + watermarks - inline-block sizes to image */}
                    <div className="relative inline-block">
                      <img
                        ref={mainImageRef}
                        src={currentVariant.url}
                        alt={`${propertyAddress} - ${roomName} - Variant ${currentVariantIndex + 1}`}
                        className="block max-w-full max-h-full"
                        style={{ maxWidth: 'calc(75vw - 4rem)', maxHeight: 'calc(100vh - 8rem)' }}
                      />

                      {/* Watermark overlay - fills the wrapper (same size as image) */}
                      {renderWatermarkOverlay(mainImageDimensions, includeAgencyWatermark, includeWyskoWatermark)}
                    </div>

                    {/* Previous/Next Navigation - Always visible on mobile */}
                    {effectiveVariants.length > 1 && (
                      <>
                        <button
                          onClick={handlePrevious}
                          className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-full bg-white/20 lg:bg-white/10 hover:bg-white/30 transition-all opacity-100 lg:opacity-0 lg:group-hover:opacity-100 backdrop-blur-sm"
                        >
                          <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6 text-white" strokeWidth={2} />
                        </button>
                        <button
                          onClick={handleNext}
                          className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-full bg-white/20 lg:bg-white/10 hover:bg-white/30 transition-all opacity-100 lg:opacity-0 lg:group-hover:opacity-100 backdrop-blur-sm"
                        >
                          <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-white" strokeWidth={2} />
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Comparison Toggle Button - Bottom Center (hidden when no variants) */}
                {originalUrl && !hasNoVariants && (
                  <button
                    onClick={() => setIsComparisonMode(!isComparisonMode)}
                    className={`absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-full text-white text-[13px] font-medium shadow-lg transition-all backdrop-blur-sm border-2 flex items-center gap-2 ${isComparisonMode
                      ? 'bg-blue-600 hover:bg-blue-700 border-blue-400'
                      : 'bg-white/10 hover:bg-white/20 border-white/30'
                      }`}
                  >
                    <Split className="w-4 h-4" strokeWidth={2} />
                    {isComparisonMode ? 'Ukryj porównanie' : 'Porównaj z oryginałem'}
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Control Panel */}
            <div className="relative w-full lg:w-[25%] flex-1 lg:flex-none bg-card flex flex-col overflow-hidden">
              {/* Close Button - Top Right, Bigger */}
              <button
                onClick={handleCloseAttempt}
                className="hidden lg:flex absolute top-4 right-4 w-10 h-10 items-center justify-center rounded-full bg-transparent hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-20"
              >
                <X className="w-5 h-5 text-foreground/80" strokeWidth={2} />
              </button>

              {/* Header */}
              <div className="p-4 lg:p-6 border-b border-border">
                {/* Title - Hidden on mobile since we have mobile header */}
                <div className="hidden lg:flex items-start justify-between mb-4 pr-12">
                  <div className="flex-1">
                    <h2 className="text-[16px] font-medium mb-1">
                      {propertyAddress} / {roomName}
                    </h2>
                  </div>
                </div>

                {/* Header Actions */}
                <div className="flex gap-2">


                  <Button
                    variant={!currentPropertyId ? "default" : "outline"}
                    size="sm"
                    className={`flex-1 gap-2 text-[12px] ${!currentPropertyId ? "bg-amber-600 hover:bg-amber-700 text-white border-none" : ""}`}
                    onClick={() => setIsMoveDialogOpen(true)}
                  >
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    {!currentPropertyId ? "Przypisz do nieruchomości" : "Przenieś do..."}
                  </Button>

                  {/* Favorite Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-2 text-[12px] ${isFavorite ? 'text-destructive hover:text-destructive bg-destructive/10 hover:bg-destructive/20 border-destructive/20' : ''}`}
                    onClick={onToggleFavorite}
                    disabled={!currentPropertyId} // Disable favorite if unsorted
                  >
                    <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} strokeWidth={2} />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                  </Button>
                </div>
              </div>

              {/* Variants Grid */}
              <div className="flex-1 p-4 lg:p-6 overflow-y-auto scrollbar-hide">
                {hasNoVariants ? (
                  /* Error / Empty State — no variants generated */
                  <div className="flex flex-col items-center gap-3 py-6 text-center">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-500" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-foreground">
                        Nie udało się wygenerować wariantów
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Modele AI były chwilowo niedostępne. Spróbuj ponownie.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="w-full gap-2 text-[12px] mt-2"
                      onClick={() => {
                        onRerender?.();
                      }}
                    >
                      Renderuj od nowa (1 <Crown className="w-3 h-3 text-yellow-500 fill-current" />)
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-[13px] font-medium mb-3 text-muted-foreground">
                      Wygenerowane warianty
                    </h3>
                    {/* Horizontal scroll on mobile, grid on desktop */}
                    <div className="flex lg:grid gap-3 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 lg:grid-cols-2 scrollbar-hide">
                      {effectiveVariants.length === 0 ? (
                        /* Loading Skeletons for Variants */
                        Array.from({ length: 4 }).map((_, i) => (
                          <div key={i} className="w-20 h-20 lg:w-auto lg:h-auto aspect-square rounded-lg bg-gray-100 animate-pulse flex-shrink-0" />
                        ))
                      ) : (
                        <>
                          {effectiveVariants.map((variant, index) => (
                            <button
                              key={variant.id}
                              onClick={() => setCurrentVariantIndex(index)}
                              className={`relative w-20 h-20 lg:w-auto lg:h-auto aspect-square rounded-lg overflow-hidden transition-all hover:scale-105 flex-shrink-0 ${currentVariantIndex === index
                                ? 'ring-4 ring-foreground ring-offset-2'
                                : 'ring-1 ring-border'
                                }`}
                            >
                              <img
                                src={variant.url}
                                alt={variant.label}
                                className="w-full h-full object-cover"
                              />
                              {currentVariantIndex === index && (
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                  <div className="w-3 h-3 rounded-full bg-white"></div>
                                </div>
                              )}
                            </button>
                          ))}
                          {effectiveVariants.length === 1 && onRetryVariant && (
                            <button
                              onClick={onRetryVariant}
                              disabled={isRetryingVariant}
                              className="relative w-20 h-20 lg:w-auto lg:h-auto aspect-square rounded-lg
                                border-2 border-dashed border-primary/30 hover:border-primary/60
                                flex flex-col items-center justify-center gap-1 transition-all
                                flex-shrink-0 bg-primary/5 hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isRetryingVariant ? (
                                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4 text-primary/60" />
                                  <span className="text-[9px] text-primary/60 font-medium text-center leading-tight hidden lg:block">
                                    Wygeneruj wariant
                                  </span>
                                  <span className="text-[8px] text-muted-foreground hidden lg:block">
                                    0 kredytów
                                  </span>
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Rerender Button */}
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2 text-[12px] border-border hover:bg-muted"
                        onClick={() => {
                          onRerender?.();
                          // Note: We do NOT close the modal here.
                          // The parent component handles the sequence:
                          // 1. Prepare Wizard state
                          // 2. Close Preview
                          // 3. Open Wizard
                          // This prevents race conditions and focus issues.
                        }}
                      >
                        Renderuj od nowa (1 <Crown className="w-3 h-3 text-yellow-500 fill-current" />)
                      </Button>
                    </div>
                  </>
                )}

                {/* Render Settings Section - Hidden on mobile */}
                <div className="hidden lg:block mt-8 space-y-3">
                  <h3 className="text-[13px] font-medium text-muted-foreground">
                    Ustawienia renderu
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {settingsTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-[11px]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Metadata Section - Hidden on mobile */}
                <div className="hidden lg:block mt-8 space-y-3">
                  <h3 className="text-[13px] font-medium text-muted-foreground">
                    Szczegóły techniczne
                  </h3>
                  <div className="space-y-2 text-[12px]">
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Rozdzielczość</span>
                      {isLoadingMetadata ? (
                        <span className="w-20 h-4 bg-muted animate-pulse rounded" />
                      ) : (
                        <span>{imageMetadata ? `${imageMetadata.width} × ${imageMetadata.height}` : '—'}</span>
                      )}
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Format</span>
                      {isLoadingMetadata ? (
                        <span className="w-12 h-4 bg-muted animate-pulse rounded" />
                      ) : (
                        <span>{imageMetadata?.format || '—'}</span>
                      )}
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Model AI</span>
                      {isLoadingMetadata ? (
                        <span className="w-24 h-4 bg-muted animate-pulse rounded" />
                      ) : (
                        <span>{imageMetadata?.source || '—'}</span>
                      )}
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Data utworzenia</span>
                      {isLoadingMetadata ? (
                        <span className="w-20 h-4 bg-muted animate-pulse rounded" />
                      ) : (
                        <span>{imageMetadata?.createdAt || '—'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Watermark Options - moved below technical details */}
                {effectiveVariants.length > 0 && (
                  <div className="hidden lg:block pt-4 mt-4 border-t border-border">
                    <h3 className="text-[13px] font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Droplets className="w-4 h-4" />
                      Znaki wodne
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label htmlFor="wysko-watermark-toggle" className="text-xs cursor-pointer">
                          Znak Wysko
                        </label>
                        <Switch
                          checked={includeWyskoWatermark}
                          onCheckedChange={setIncludeWyskoWatermark}
                          id="wysko-watermark-toggle"
                        />
                      </div>
                      {settings?.watermark_logo_url && (
                        <div className="flex items-center justify-between">
                          <label htmlFor="agency-watermark-toggle" className="text-xs cursor-pointer">
                            Logo agencji
                          </label>
                          <Switch
                            checked={includeAgencyWatermark}
                            onCheckedChange={setIncludeAgencyWatermark}
                            id="agency-watermark-toggle"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer - Fixed Actions */}
              <div className="p-4 lg:p-6 border-t border-border space-y-3">
                {hasNoVariants ? (
                  /* No variants — no download button, just close */
                  null
                ) : effectiveVariants.length === 0 ? (
                  <Button
                    className="w-full gap-2 shadow-sm"
                    size="lg"
                    disabled
                  >
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generowanie...
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="w-full gap-2 shadow-sm"
                        size="lg"
                        disabled={!currentVariant || isDownloading}
                      >
                        {isDownloading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Pobieranie...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" strokeWidth={2} />
                            Pobierz
                            <ChevronDown className="w-4 h-4 ml-auto" strokeWidth={2} />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[200px] z-[9999]" align="center">
                      <DropdownMenuItem
                        onClick={() => downloadAs('png')}
                        className="cursor-pointer"
                      >
                        <span className="font-medium">PNG</span>
                        <span className="ml-auto text-muted-foreground text-xs">Bezstratny, najlepsza jakość</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => downloadAs('jpg')}
                        className="cursor-pointer"
                      >
                        <span className="font-medium">JPG</span>
                        <span className="ml-auto text-muted-foreground text-xs">Kompresja, mniejszy plik</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => downloadAs('webp')}
                        className="cursor-pointer"
                      >
                        <span className="font-medium">WebP</span>
                        <span className="ml-auto text-muted-foreground text-xs">Nowoczesny, zalecany</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Save Confirmation Dialog */}
      <AlertDialog open={isCloseConfirmOpen} onOpenChange={setIsCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Czy na pewno chcesz zamknąć?</AlertDialogTitle>
            <AlertDialogDescription>
              Ten render nie jest przypisany do żadnej nieruchomości. Jeśli zamkniesz teraz, utworzymy nową nieruchomość z dzisiejszą datą i zapiszemy tam to zdjęcie.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsCloseConfirmOpen(false)}>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setIsCloseConfirmOpen(false);
              onAutoSaveAndClose?.();
            }}>
              Zapisz i zamknij
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteFileDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        fileName={roomName}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          onOpenChange(false);
          if (onDelete) {
            onDelete();
          }
        }}
      />

      {/* Move Property Dialog (Command Palette Style) */}
      <CommandDialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
        <DialogTitle className="sr-only">Wybierz nieruchomość docelową</DialogTitle>
        <DialogDescription className="sr-only">Wybierz nieruchomość, do której chcesz przenieść zdjęcie</DialogDescription>
        <CommandInput placeholder="Szukaj nieruchomości..." />
        <CommandList>
          <CommandEmpty>Nie znaleziono nieruchomości.</CommandEmpty>
          <CommandGroup heading="Dostępne nieruchomości">
            {availableProperties
              .filter(p => p.id !== currentPropertyId)
              .map((property) => (
                <CommandItem
                  key={property.id}
                  onSelect={() => {
                    if (onMove) {
                      onMove(property.id);
                      setIsMoveDialogOpen(false);
                      onOpenChange(false);
                    }
                  }}
                  className="flex items-center gap-3 p-2 cursor-pointer"
                >
                  <div className="w-10 h-8 rounded-md bg-muted overflow-hidden flex-shrink-0">
                    <img
                      src={property.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium text-[13px]">{property.address}</span>
                    <span className="text-[11px] text-muted-foreground">{property.photoCount} zdjęć</span>
                  </div>
                  {currentPropertyId === property.id && (
                    <span className="ml-auto text-[11px] text-muted-foreground">(Obecna)</span>
                  )}
                </CommandItem>
              ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}